import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Home, MapPin, ArrowRight, Filter, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { projects } from '../data/projects';
import { fadeUp, staggerContainer, defaultViewport } from '../components/ui/AnimationVariants';

const categories = [
  { value: 'all',        label: 'All Projects' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'residential',label: 'Residential' },
  { value: 'mixed-use',  label: 'Mixed-Use' },
];

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const filteredProjects =
    filter === 'all' ? projects : projects.filter((p) => p.category === filter);

  return (
    <div>
      {/* Hero */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Our Projects</motion.p>
          <motion.h1 variants={fadeUp} className="font-heading text-h1 text-white mt-4 max-w-3xl">
            Our Projects
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            Active and completed electrical projects across Massachusetts — commercial, mixed-use, and residential. Click any project to review the work directly.
          </motion.p>
        </div>
      </motion.section>

      {/* Filter */}
      <section className="bg-navy-900 border-b border-navy-600 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="h-4 w-4 text-slate-400" />
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setFilter(cat.value)}
                className={`px-5 py-2 font-heading uppercase text-sm tracking-wide transition-colors duration-200 ${
                  filter === cat.value
                    ? 'bg-primary-500 text-white'
                    : 'border border-navy-600 text-slate-400 hover:border-primary-500 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                variants={fadeUp}
                whileHover={{ x: 4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="group relative overflow-hidden border border-navy-600 border-l-2 border-l-navy-600 hover:border-l-primary-500 transition-colors duration-200"
              >
                {/* Thumbnail */}
                <div className="h-56 overflow-hidden bg-navy-800">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-300"
                  />
                </div>

                <div className="p-6">
                  {/* Category + Date */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center gap-1.5 text-primary-500 font-heading uppercase text-xs tracking-wide">
                      {project.category === 'residential'
                        ? <Home className="h-3.5 w-3.5" />
                        : <Building2 className="h-3.5 w-3.5" />}
                      {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                    </span>
                    <span className="text-xs text-slate-400">{project.date}</span>
                  </div>

                  <h3 className="font-heading text-white uppercase text-xl">{project.title}</h3>

                  {/* Location + Scale */}
                  <div className="space-y-1 mt-3">
                    <div className="flex items-center text-slate-400 text-sm">
                      <MapPin className="h-4 w-4 mr-2 text-primary-500 flex-shrink-0" />
                      {project.location}
                    </div>
                    <div className="flex items-center text-slate-400 text-sm">
                      <Building2 className="h-4 w-4 mr-2 text-primary-500 flex-shrink-0" />
                      {project.scale}
                    </div>
                  </div>

                  <p className="text-slate-400 text-sm leading-relaxed mt-4">{project.description}</p>

                  {/* Feature tags */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {project.features.map((f, i) => (
                      <span key={i} className="text-xs border border-navy-600 text-slate-400 px-2.5 py-1">
                        {f}
                      </span>
                    ))}
                  </div>

                  {/* Links */}
                  <div className="flex items-center gap-6 mt-5">
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-primary-500 hover:text-primary-400 font-heading uppercase text-sm tracking-wide transition-colors"
                    >
                      Visit Project
                      <ExternalLink className="ml-1.5 h-4 w-4" />
                    </a>
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-slate-400 hover:text-white font-heading uppercase text-sm tracking-wide transition-colors"
                    >
                      Discuss your project
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Stats */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-900"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Project Statistics</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
            Project Statistics
          </motion.h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 mt-16">
            {[
              { value: '500+',  label: 'Projects Delivered' },
              { value: '$100M+',label: 'Total Project Volume' },
              { value: '15+',   label: 'Years in Commercial Work' },
              { value: 'MA',    label: 'Massachusetts-Based' },
            ].map((stat, i) => (
              <motion.div key={i} variants={fadeUp} className="border-t border-navy-600 pt-8 pb-8 pr-8">
                <div className="font-heading text-h2 text-white leading-none">{stat.value}</div>
                <div className="text-slate-400 text-sm mt-3">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Get Started</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4 max-w-3xl">
            Your Project on This List?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            If your project is in Massachusetts and needs a commercial electrical contractor with consistent manpower and direct field accountability, contact us to discuss scope and schedule.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10">
            <Link to="/contact" className="btn-primary inline-flex items-center">
              Discuss Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Projects;
